/**
 * Subscription Context
 * 
 * Manages subscription state for the app:
 * - Checks subscription status on load
 * - Provides subscription info to components
 * - Shows paywall if no active subscription
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getFreshToken, isTransientNetworkError } from '../utils/auth';
import { APP_CONFIG } from '../config/app';
import { useAuth } from '../components/AuthProvider';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

// API base URL
const API_URL = APP_CONFIG.apiUrl;

/**
 * Fetch subscription status from backend
 */
function callerEmailHeader() {
  try {
    const email = (window.Auth?.getUser?.()?.email || '').trim().toLowerCase();
    return email ? { 'X-User-Email': email } : {};
  } catch {
    return {};
  }
}

async function fetchSubscriptionStatus() {
  const token = await getFreshToken();
  
  const response = await fetch(`${API_URL}/subscription/status`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      ...callerEmailHeader(),
    },
    signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout
      ? AbortSignal.timeout(20000)
      : undefined,
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch subscription status');
  }
  
  return response.json();
}

async function updateComplimentaryList(action, email) {
  const token = await getFreshToken();
  const response = await fetch(`${API_URL}/subscription/status`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...callerEmailHeader(),
    },
    body: JSON.stringify({ action, email }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Could not update complimentary list');
  }
  return data;
}

/**
 * Create a checkout session
 */
async function createCheckoutSession(priceType = 'monthly', workspaceId, options = {}) {
  const token = await getFreshToken();
  
  const response = await fetch(`${API_URL}/subscription/checkout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      priceType,
      ...(workspaceId && workspaceId !== 'personal' ? { workspaceId } : {}),
      ...(options.addSeat ? { addSeat: true } : {}),
    }),
  });
  
  if (!response.ok) {
    throw new Error('Failed to create checkout session');
  }
  
  return response.json();
}

/**
 * Get customer portal link
 */
async function getPortalLink() {
  const token = await getFreshToken();
  
  const response = await fetch(`${API_URL}/subscription/portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to get portal link');
  }
  
  return response.json();
}

async function openBillingUrl(url) {
  if (!url) return;
  if (typeof window !== 'undefined' && window.electronAPI?.openExternal) {
    await window.electronAPI.openExternal(url);
    return;
  }
  window.location.href = url;
}

export const SubscriptionProvider = ({ children }) => {
  const { loading: authLoading, authenticated, refreshWorkspaces } = useAuth();

  const [subscription, setSubscription] = useState({
    loading: true,
    hasAccess: false,
    complimentary: false,
    complimentaryEmails: [],
    status: 'none',
    isTrialing: false,
    trialEndsAt: null,
    currentPeriodEnd: null,
    plan: null,
    planId: null,
    quantity: null,
    error: null,
  });

  // Check subscription status. A wake-up failure used to clear `loading` with
  // hasAccess still false, which drops the startup overlay and shows the paywall.
  const checkSeqRef = useRef(0);
  const checkSubscription = useCallback(async () => {
    const seq = ++checkSeqRef.current;
    for (;;) {
      if (seq !== checkSeqRef.current) return null;
      try {
        setSubscription(prev => ({ ...prev, loading: true, error: null }));

        const data = await fetchSubscriptionStatus();
        if (seq !== checkSeqRef.current) return null;

        setSubscription({
          loading: false,
          hasAccess: data.hasAccess || false,
          complimentary: !!data.complimentary,
          complimentaryEmails: Array.isArray(data.complimentaryEmails)
            ? data.complimentaryEmails
            : [],
          status: data.status || 'none',
          isTrialing: data.isTrialing || false,
          trialEndsAt: data.trialEndsAt ? new Date(data.trialEndsAt) : null,
          currentPeriodEnd: data.currentPeriodEnd ? new Date(data.currentPeriodEnd) : null,
          plan: data.plan || null,
          planId: data.planId || null,
          quantity: data.quantity || null,
          error: null,
        });

        return data;
      } catch (error) {
        if (seq !== checkSeqRef.current) return null;
        if (isTransientNetworkError(error)) {
          console.warn('Subscription check failed while offline, retrying:', error);
          await new Promise((resolve) => setTimeout(resolve, 1500));
          continue;
        }
        console.error('Failed to check subscription:', error);
        setSubscription(prev => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
        return null;
      }
    }
  }, []);

  // Start checkout flow
  const startCheckout = useCallback(async (priceType = 'monthly', workspaceId, options = {}) => {
    try {
      const data = await createCheckoutSession(priceType, workspaceId, options);
      if (data.switched) {
        await checkSubscription();
        await refreshWorkspaces?.();
        return data;
      }
      if (data.url) {
        await openBillingUrl(data.url);
      }
      return data;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      throw error;
    }
  }, [checkSubscription, refreshWorkspaces]);

  // Open customer portal
  const openPortal = useCallback(async () => {
    try {
      const data = await getPortalLink();
      if (data.url) {
        await openBillingUrl(data.url);
      }
      return data;
    } catch (error) {
      console.error('Failed to open portal:', error);
      throw error;
    }
  }, []);

  // After full app quit/reopen, AuthProvider runs ensureSessionReady first. Wait until
  // that finishes so we do not call getFreshToken before tokens are hydrated from Electron.
  useEffect(() => {
    if (authLoading) return;
    if (!authenticated) {
      setSubscription((prev) => ({
        ...prev,
        loading: false,
        hasAccess: false,
        complimentary: false,
        complimentaryEmails: [],
        error: null,
      }));
      return;
    }
    checkSubscription();
  }, [authLoading, authenticated, checkSubscription]);

  // Check for checkout success/cancel in URL
  useEffect(() => {
    if (authLoading || !authenticated) return;
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');

    if (checkoutStatus === 'success') {
      checkSubscription();
      refreshWorkspaces?.();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutStatus === 'canceled') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [authLoading, authenticated, checkSubscription, refreshWorkspaces]);

  const addComplimentaryEmail = useCallback(async (email) => {
    const data = await updateComplimentaryList('add', email);
    setSubscription((prev) => ({
      ...prev,
      complimentary: data.complimentary ?? prev.complimentary,
      complimentaryEmails: data.complimentaryEmails || prev.complimentaryEmails,
      hasAccess: data.hasAccess ?? prev.hasAccess,
    }));
    return data;
  }, []);

  const removeComplimentaryEmail = useCallback(async (email) => {
    const data = await updateComplimentaryList('remove', email);
    setSubscription((prev) => ({
      ...prev,
      complimentary: data.complimentary ?? prev.complimentary,
      complimentaryEmails: data.complimentaryEmails || prev.complimentaryEmails,
      hasAccess: data.hasAccess ?? prev.hasAccess,
    }));
    return data;
  }, []);

  const value = {
    ...subscription,
    checkSubscription,
    startCheckout,
    openPortal,
    addComplimentaryEmail,
    removeComplimentaryEmail,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;

